// src/pages/admin/AdminDashboard.jsx
//
// NOTE (UX audit ISSUE 8): tour de onboarding intencionalmente omitido en
// AdminDashboard. Los admins son usuarios técnicos del producto que no
// necesitan walkthrough. El tour solo aparece en ClientDashboard.
//
// FASE 1.C — Renombrado desde src/App.jsx legacy.
// Cambios mínimos respecto al legacy:
//   - paths relativos ajustados (src/pages/admin/ es 2 niveles más profundo)
//   - useUserDoc → useAuth (consume el AuthContext nuevo)
//   - isAdmin se obtiene del context en vez de calcularlo localmente
// JSX interno y lógica de picks/state intactos.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import '../../index.css';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import LivePage from '../../components/LivePage';
import ConfigPage from '../../components/ConfigPage';
import PerformanceLab from '../../components/PerformanceLab';
import SignalsHistory from '../../components/SignalsHistory';
import { useAuth } from '../../auth/AuthContext';
import { normalizeLeague } from '../../utils';
import { DashboardThemeProvider } from '../../theme/DashboardTheme';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [picks, setPicks]           = useState([]);
  const [connected, setConnected]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [page, setPage]             = useState(() => localStorage.getItem('ee_route') || 'live');
  const [selectedLeague, setLeague] = useState(() => localStorage.getItem('ee_league') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tick, setTick]             = useState(0);

  // FASE 1.C: usuario y rol vienen del AuthContext (no de useUserDoc).
  const { user: authUser, userData, isAdmin } = useAuth();
  const user = {
    uid: authUser?.uid || null,
    displayName: userData?.nombre || authUser?.displayName || localStorage.getItem('mee_name') || 'Trader',
    email: authUser?.email || null,
  };

  // Persistir ruta y liga
  useEffect(() => { localStorage.setItem('ee_route', page); }, [page]);
  useEffect(() => { localStorage.setItem('ee_league', selectedLeague); }, [selectedLeague]);

  // Auto-reset defensivo: si selectedLeague no aparece en los picks
  // actuales por más de 5s, volver a 'all'. Cubre el caso de localStorage
  // con valor stale o liga que ya no tiene partidos en vivo.
  useEffect(() => {
    if (selectedLeague === 'all' || !selectedLeague) return;
    if (!picks || picks.length === 0) return;

    const target = normalizeLeague(selectedLeague);
    const ligasDisponibles = new Set(
      picks.map((p) => normalizeLeague(p.liga || p.league)).filter(Boolean)
    );
    if (ligasDisponibles.has(target)) return;

    const timer = setTimeout(() => {
      const stillNoMatch = picks.every(
        (p) => normalizeLeague(p.liga || p.league) !== target
      );
      if (stillNoMatch) {
        // eslint-disable-next-line no-console
        console.log('[ee_league] Reset filter — liga stale:', selectedLeague);
        setLeague('all');
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [picks, selectedLeague]);

  // Guard admin: si la pagina actual es 'performance' y el usuario no es admin,
  // forzamos volver a 'live'. Cubre acceso por localStorage previo o por URL.
  useEffect(() => {
    if (page === 'performance' && !isAdmin) {
      setPage('live');
    }
  }, [page, isAdmin]);

  // Firebase listener
  useEffect(() => {
    const picksRef = ref(db, 'picks');
    const unsub = onValue(picksRef, snapshot => {
      const data = snapshot.val();
      if (!data) { setConnected(false); setPicks([]); return; }
      const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
      list.sort((a, b) => {
        const rank = v => {
          const d = (v.decision || '').toLowerCase();
          if (d.includes('under') || d.includes('over')) return 0;
          if (d.includes('early')) return 2;
          return 1;
        };
        return rank(a) - rank(b);
      });
      setPicks(list);
      setConnected(true);
      if (list[0]?.timestamp) setLastUpdate(new Date(list[0].timestamp));
    }, () => setConnected(false));
    return () => unsub();
  }, []);

  // Tick cada 5s para refrescar timestamps
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  function getAgeStr() {
    if (!lastUpdate) return '—';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 10) return 'ahora';
    if (secs < 60) return `hace ${secs}s`;
    return `hace ${Math.floor(secs / 60)}m`;
  }

  function getAgeColor() {
    if (!lastUpdate) return 'var(--muted)';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 15) return 'var(--pos)';
    if (secs < 30) return 'var(--warn)';
    return 'var(--neg)';
  }

  function handleNav(p, league) {
    setPage(p);
    if (league) setLeague(league);
    setSidebarOpen(false);
    // 2026-05-10 RT2: paridad con ClientDashboard. Click "Historial" en
    // sidebar navega al URL /historial real (no se queda en /admin/dashboard
    // con tab interno). Si admin elige Live, sigue en /admin/dashboard.
    // Otras pestañas admin (performance, config) son tabs internos del admin
    // panel y no tienen URL dedicada.
    if (p === 'signals') {
      navigate('/historial');
    }
  }

  return (
    <DashboardThemeProvider>
    <div className="app-layout app">
      <Sidebar
        page={page}
        setPage={p => handleNav(p)}
        selectedLeague={selectedLeague}
        setLeague={l => handleNav('live', l)}
        picks={picks}
        user={user}
        isAdmin={isAdmin}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="main-content main">
        <Topbar
          connected={connected}
          lastUpdate={lastUpdate}
          getAgeStr={getAgeStr}
          getAgeColor={getAgeColor}
          onMenuOpen={() => setSidebarOpen(true)}
          picks={picks}
        />

        {page === 'live'        && <LivePage picks={picks} connected={connected} selectedLeague={selectedLeague} user={user} userData={userData} tick={tick} />}
        {page === 'performance' && isAdmin && <PerformanceLab />}
        {page === 'signals'     && <SignalsHistory />}
        {page === 'config'      && <ConfigPage user={user} />}
      </div>
    </div>
    </DashboardThemeProvider>
  );
}
